import { find, some, uniqBy } from 'lodash';

import type { SocialProfileNetworkType, SocialProfileType } from '@/types/socialProfileType';

import { postsSortTypes, storiesSortTypes } from '@/lib/posts/postSortTypes';

type GetPostTypesParams = {
    albumType?: 'stories';
    isGroup: boolean;
    selectedSocialProfiles?: (string | number)[];
    groupSocialProfiles?: SocialProfileType[];
    network?: SocialProfileNetworkType;
    translationFunction: (key: string) => string;
};

type PostTypeOption = ArrayElement<
    ValueOfObject<ReturnType<typeof postsSortTypes>> | ReturnType<typeof storiesSortTypes>
>;
import { TimeService } from '@/utils/services/TimeService';

import { get, post, put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { ALBUM, ALBUMS } from '@/lib/routes/apiEndpoints';

type AlbumCriteria = {
    started_at?: string | null;
    ended_at?: string | null;
    social_profiles?: (string | number)[];
    [key: string]: any;
};

type AlbumInfo = {
    id?: string | number;
    name: string;
    type: string;
    criteria?: AlbumCriteria;
};

type SubmitAlbumParams = {
    albumInfo: AlbumInfo;
    isGroup: boolean;
    activeSocialProfileId?: string | number;
    activeGroupId?: string | number;
    userTimezone: string;
};

type AlbumPayload = {
    name: string;
    type: string;
    criteria: AlbumCriteria;
    social_profile_id?: string | number;
    group_id?: string | number;
};

type SubmitAlbumResult = {
    success: boolean;
    albumId?: string | number;
    error?: string;
};

type ErrorResponse = {
    response?: {
        data?: {
            errors?: {
                name?: string[];
            };
        };
    };
};

class AlbumFunnelModalService {
    getAllProfileIds(availableProfiles: SocialProfileType[]): (string | number)[] {
        return availableProfiles.map((profile) => profile.id);
    }

    handleAllOptionClick(
        allAreSelected: boolean,
        availableProfiles: SocialProfileType[]
    ): (string | number)[] {
        return allAreSelected ? [] : this.getAllProfileIds(availableProfiles);
    }

    toggleProfileSelection(
        profileId: string | number,
        currentSelectedIds: (string | number)[]
    ): (string | number)[] {
        const isCurrentlySelected = currentSelectedIds.includes(profileId);

        return isCurrentlySelected
            ? currentSelectedIds.filter((id) => id !== profileId)
            : [...currentSelectedIds, profileId];
    }

    handleIndividualSelection(
        lastClickedValue: string | number | undefined,
        currentSelectedIds: (string | number)[]
    ): (string | number)[] {
        if (!lastClickedValue || lastClickedValue === 'all') {
            return currentSelectedIds;
        }

        return this.toggleProfileSelection(lastClickedValue, currentSelectedIds);
    }

    private getStoriesPostTypes(translationFunction: (key: string) => string): PostTypeOption[] {
        return storiesSortTypes(translationFunction);
    }

    private getPostTypesForNetwork(
        network: SocialProfileNetworkType,
        translationFunction: (key: string) => string
    ): PostTypeOption[] {
        return postsSortTypes(translationFunction)[network] || [];
    }

    private findGroupProfileById(
        profileId: string | number,
        groupProfiles: SocialProfileType[]
    ): SocialProfileType | undefined {
        return find(groupProfiles, { id: profileId }) as SocialProfileType | undefined;
    }

    private composeGroupPostTypes(
        selectedProfiles: (string | number)[],
        groupProfiles: SocialProfileType[],
        translationFunction: (key: string) => string
    ): PostTypeOption[] {
        return selectedProfiles.reduce((acc: PostTypeOption[], profileId) => {
            const groupProfile = this.findGroupProfileById(profileId, groupProfiles);

            if (!groupProfile?.social_network_type) {
                return acc;
            }

            const networkTypes = this.getPostTypesForNetwork(
                groupProfile.social_network_type,
                translationFunction
            );

            return acc.concat(networkTypes);
        }, []);
    }

    private sortGroupPostTypes(groupPostTypes: PostTypeOption[]): PostTypeOption[] {
        //We sort options in order to always have 'all" as first value & other values in alphabetical order
        return groupPostTypes.sort((a, b) => {
            if (a.value === 'all') return -1;
            if (b.value === 'all') return 1;
            return a.label.localeCompare(b.label);
        });
    }

    private getGroupPostTypes(
        selectedSocialProfiles: (string | number)[] | undefined,
        groupSocialProfiles: SocialProfileType[] | undefined,
        translationFunction: (key: string) => string
    ): PostTypeOption[] {
        if (!selectedSocialProfiles?.length || !groupSocialProfiles?.length) {
            return [];
        }

        let groupPostTypes = this.composeGroupPostTypes(
            selectedSocialProfiles,
            groupSocialProfiles,
            translationFunction
        );

        groupPostTypes = uniqBy(groupPostTypes, 'value');

        return this.sortGroupPostTypes(groupPostTypes);
    }

    private getSingleProfilePostTypes(
        network: SocialProfileNetworkType | undefined,
        translationFunction: (key: string) => string
    ): PostTypeOption[] {
        if (!network) {
            return [];
        }

        return this.getPostTypesForNetwork(network, translationFunction);
    }

    getPostTypes(params: GetPostTypesParams): PostTypeOption[] {
        const {
            albumType,
            isGroup,
            selectedSocialProfiles,
            groupSocialProfiles,
            network,
            translationFunction
        } = params;

        if (albumType === 'stories') {
            return this.getStoriesPostTypes(translationFunction);
        }

        if (isGroup) {
            return this.getGroupPostTypes(
                selectedSocialProfiles,
                groupSocialProfiles,
                translationFunction
            );
        }

        return this.getSingleProfilePostTypes(network, translationFunction);
    }

    validateSocialProfiles(
        isGroup: boolean,
        socialProfiles?: (string | number)[]
    ): { isValid: boolean; error?: string } {
        if (isGroup && (!socialProfiles || socialProfiles.length === 0)) {
            return {
                isValid: false,
                error: 'social_profiles_required'
            };
        }

        return { isValid: true };
    }

    formatAlbumCriteria(criteria: AlbumCriteria | undefined, userTimezone: string): AlbumCriteria {
        if (!criteria) {
            return {
                hashtags: [],
                labels: [],
                post_types: [],
                social_profiles: []
            };
        }

        return {
            ...criteria,
            hashtags: Array.isArray(criteria.hashtags) ? criteria.hashtags : [],
            labels: Array.isArray(criteria.labels) ? criteria.labels : [],
            post_types: Array.isArray(criteria.post_types) ? criteria.post_types : [],
            social_profiles: Array.isArray(criteria.social_profiles)
                ? criteria.social_profiles
                : [],
            started_at: TimeService.formatDateWithTimezone(criteria.started_at, userTimezone),
            ended_at: TimeService.formatDateWithTimezone(criteria.ended_at, userTimezone)
        };
    }

    composePayload(params: SubmitAlbumParams): AlbumPayload {
        const { albumInfo, isGroup, activeSocialProfileId, activeGroupId, userTimezone } = params;

        const payload: AlbumPayload = {
            name: albumInfo.name,
            type: albumInfo.type,
            criteria: this.formatAlbumCriteria(albumInfo.criteria, userTimezone)
        };

        if (isGroup) {
            payload.group_id = activeGroupId;
        } else {
            payload.social_profile_id = activeSocialProfileId;
            delete payload.criteria?.social_profiles;
        }

        return payload;
    }

    async performAlbumRequest(
        albumId: string | number | undefined,
        payload: AlbumPayload
    ): Promise<{ data?: { id: string | number } }> {
        if (albumId) {
            await put(injectEndpointUrlParams(ALBUM, { ALBUM_ID: albumId }), payload);
            return {};
        }

        return await post(ALBUMS, payload);
    }

    isUniqueNameError(error: ErrorResponse): boolean {
        return error?.response?.data?.errors?.name?.includes('unique') ?? false;
    }

    async isAlbumNameDuplicated(params: {
        name?: string;
        isGroup?: boolean;
        activeSocialProfileId?: string | number;
        activeGroupId?: string | number;
    }): Promise<boolean> {
        const { name, activeSocialProfileId, activeGroupId } = params;

        if (!name || name.trim() === '') {
            return false;
        }

        const queryParams: Record<string, string> = {
            name: name.trim(),
            per_page: '1'
        };

        if (activeGroupId) {
            queryParams.group_id = activeGroupId.toString();
        } else if (activeSocialProfileId) {
            queryParams.social_profile_id = activeSocialProfileId.toString();
        }

        const response = await get(ALBUMS, queryParams);

        const isNameDuplicated = some(
            response?.data?.data,
            (album) => album.name?.toLowerCase() === name.trim().toLowerCase()
        );

        return isNameDuplicated;
    }

    async submitAutomaticAlbum(params: SubmitAlbumParams): Promise<SubmitAlbumResult> {
        const { albumInfo, isGroup } = params;

        const validationResult = this.validateSocialProfiles(
            isGroup,
            albumInfo?.criteria?.social_profiles
        );

        if (!validationResult.isValid) {
            return {
                success: false,
                error: validationResult.error
            };
        }

        try {
            const payload = this.composePayload(params);
            const response = await this.performAlbumRequest(albumInfo?.id, payload);
            const albumId = albumInfo?.id || response?.data?.id;

            return {
                success: true,
                albumId
            };
        } catch (error) {
            const errorType = this.isUniqueNameError(error as ErrorResponse)
                ? 'unique_name'
                : 'generic';

            return {
                success: false,
                error: errorType
            };
        }
    }
}

const albumFunnelModalService = new AlbumFunnelModalService();
export { albumFunnelModalService as AlbumFunnelModalService };
